import { useEffect, useMemo, useRef, useState } from 'react';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { buildCheckoutScheduleDateOptions } from '../utils/checkoutScheduleDates';
import { brandLogoUrl as resolveBrandLogoUrl } from '../branding';
import { validateCheckoutCustomerDetails } from '../utils/checkoutCustomerValidation';
import { resolveStripeChargeAmountMajor } from '../utils/paymentTypes';
import { fetchPostcodeSuggestions, isCompleteUkPostcode, normalizePostcode } from '../utils/deliveryAddress';

const PAYMENT_FAILURE_MESSAGE = 'Failed to process payment. Please try again later.';
const PAYMENT_METHODS_UNAVAILABLE_MESSAGE = 'Payment methods not available.';

const getStripeCardErrorMessage = (error, fallback = PAYMENT_FAILURE_MESSAGE) => {
  if (!error) return fallback;
  const message = typeof error.message === 'string' ? error.message.trim() : '';
  if (message) return message;
  return fallback;
};

function ReviewOrderPageContent({
  checkoutOverlayStyle,
  setIsSidebarOpen,
  navigateTo,
  assetBase,
  storeDisplayName = 'this store',
  isScheduleExpanded,
  setIsScheduleExpanded,
  checkoutScheduleType,
  scheduledTime,
  setCheckoutScheduleType,
  scheduledDate,
  setScheduledDate,
  setScheduledTime,
  checkoutSlots = [],
  isCheckoutSlotsLoading = false,
  checkoutSlotsError = '',
  checkoutPaymentMethod,
  setCheckoutPaymentMethod,
  saveCard,
  setSaveCard,
  formatPrice,
  cartSummary,
  canPlaceOrder,
  customerFirstName,
  setCustomerFirstName,
  customerLastName,
  setCustomerLastName,
  customerPhone,
  setCustomerPhone,
  customerEmail,
  setCustomerEmail,
  isDeliveryOrder = false,
  deliveryArea = null,
  deliveryChargeAmount = 0,
  deliveryPostcode = '',
  setDeliveryPostcode,
  deliveryAddresses = [],
  selectedDeliveryAddressId = '',
  setSelectedDeliveryAddressId,
  isCheckingDeliveryPostcode = false,
  deliveryPostcodeError = '',
  onCheckDeliveryPostcode,
  onClearDeliveryAddressResult,
  onPlaceOrder,
  isPlacingOrder,
  placeOrderError,
  placeOrderSuccess,
  stripeEnabled,
  platformFeeAmount = 0,
  stripeTestChargeAmount = 0,
  paymentMethods = [],
  isPaymentMethodsLoading = false,
  onCreateStripePaymentIntent,
  stripe,
  elements,
}) {
  const [cardError, setCardError] = useState('');
  const [cardStatus, setCardStatus] = useState('');
  const [cardElementRef, setCardElementRef] = useState(null);
  const [showOrderSubmitOverlay, setShowOrderSubmitOverlay] = useState(false);
  const [detailsTouched, setDetailsTouched] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    email: false,
  });
  const [showDetailsErrors, setShowDetailsErrors] = useState(false);
  const [postcodeSuggestions, setPostcodeSuggestions] = useState([]);
  const [isPostcodeListOpen, setIsPostcodeListOpen] = useState(false);
  const [showAddressRequired, setShowAddressRequired] = useState(false);
  const postcodeBoxRef = useRef(null);
  const isStripeReady = stripeEnabled && Boolean(stripe) && Boolean(elements);
  const logoUrl = resolveBrandLogoUrl(assetBase);
  const scheduleDateOptions = useMemo(() => buildCheckoutScheduleDateOptions(), []);
  const safePlatformFeeAmount = useMemo(() => {
    const n = Number(platformFeeAmount);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [platformFeeAmount]);
  const showPlatformFeeOnCheckout = safePlatformFeeAmount > 0;
  const orderSubtotalAmount = Number(cartSummary.totalAmount || 0);
  const safeDeliveryChargeAmount = isDeliveryOrder && Number(deliveryChargeAmount) > 0 ? Number(deliveryChargeAmount) : 0;
  const deliveryMinimumOrder = isDeliveryOrder ? Number(deliveryArea?.minimumOrder || 0) : 0;
  const isBelowDeliveryMinimum = isDeliveryOrder && orderSubtotalAmount < deliveryMinimumOrder;
  const orderTotalWithSurcharge = useMemo(
    () => Math.max(0, orderSubtotalAmount + safeDeliveryChargeAmount + safePlatformFeeAmount),
    [orderSubtotalAmount, safeDeliveryChargeAmount, safePlatformFeeAmount]
  );
  const safeStripeTestChargeAmount = useMemo(() => {
    const n = Number(stripeTestChargeAmount);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [stripeTestChargeAmount]);
  const stripeCardChargeAmount = useMemo(
    () => resolveStripeChargeAmountMajor(orderTotalWithSurcharge, safeStripeTestChargeAmount),
    [orderTotalWithSurcharge, safeStripeTestChargeAmount]
  );
  const isStripeTestChargeActive =
    safeStripeTestChargeAmount > 0 && stripeCardChargeAmount !== orderTotalWithSurcharge;

  const cashMethod = useMemo(
    () => paymentMethods.find((method) => method.key === 'cash') || null,
    [paymentMethods]
  );
  const cardMethod = useMemo(
    () => paymentMethods.find((method) => method.key === 'card') || null,
    [paymentMethods]
  );
  const hasCash = Boolean(cashMethod);
  const hasCard = Boolean(cardMethod);
  const cashOnlyCheckout = hasCash && !hasCard;

  const customerDetailsValidation = useMemo(
    () =>
      validateCheckoutCustomerDetails({
        firstName: customerFirstName,
        lastName: customerLastName,
        phone: customerPhone,
        email: customerEmail,
      }),
    [customerFirstName, customerLastName, customerPhone, customerEmail]
  );

  const showFieldError = (field) =>
    (showDetailsErrors || detailsTouched[field]) && customerDetailsValidation.errors[field];

  const markDetailTouched = (field) => {
    setDetailsTouched((prev) => ({ ...prev, [field]: true }));
  };

  useEffect(() => {
    if (!paymentMethods.length) return;
    const keys = paymentMethods.map((method) => method.key);
    if (!keys.includes(checkoutPaymentMethod)) {
      setCheckoutPaymentMethod(keys[0]);
    }
  }, [paymentMethods, checkoutPaymentMethod, setCheckoutPaymentMethod]);

  useEffect(() => {
    if (checkoutScheduleType !== 'later') return;
    if (!scheduleDateOptions.length) return;
    if (!scheduleDateOptions.includes(scheduledDate)) {
      setScheduledDate(scheduleDateOptions[0]);
    }
  }, [checkoutScheduleType, scheduledDate, scheduleDateOptions, setScheduledDate]);

  // postcodes.io autocomplete -> postcode dropdown (debounced)
  useEffect(() => {
    if (!isDeliveryOrder) return undefined;
    const q = normalizePostcode(deliveryPostcode);
    if (q.replace(/\s/g, '').length < 2 || deliveryAddresses.length > 0) {
      setPostcodeSuggestions([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const list = await fetchPostcodeSuggestions(q, { signal: controller.signal });
        setPostcodeSuggestions(list);
      } catch (error) {
        if (error?.name !== 'AbortError') setPostcodeSuggestions([]);
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [deliveryPostcode, deliveryAddresses.length, isDeliveryOrder]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (postcodeBoxRef.current && !postcodeBoxRef.current.contains(event.target)) {
        setIsPostcodeListOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handlePostcodeInputChange = (value) => {
    setDeliveryPostcode(value.toUpperCase());
    setShowAddressRequired(false);
    if (deliveryAddresses.length || deliveryPostcodeError) onClearDeliveryAddressResult?.();
    setIsPostcodeListOpen(true);
  };

  const handlePickPostcode = (code) => {
    setPostcodeSuggestions([]);
    setIsPostcodeListOpen(false);
    setDeliveryPostcode(code);
    void onCheckDeliveryPostcode?.(code);
  };

  const handlePlaceOrderClick = async () => {
    setShowDetailsErrors(true);
    if (isDeliveryOrder && !selectedDeliveryAddressId) {
      setShowAddressRequired(true);
      return;
    }
    if (!customerDetailsValidation.valid) {
      setDetailsTouched({
        firstName: true,
        lastName: true,
        phone: true,
        email: true,
      });
      return;
    }

    setCardError('');
    setCardStatus('');
    let stripePaymentIntentId = '';
    let checkoutOrderRef = '';
    let cardElement = null;

    if (checkoutPaymentMethod === 'card' && stripeEnabled) {
      if (!isStripeReady) {
        setCardError('Card form is still loading. Please try again.');
        return;
      }
      cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setCardError('Card field is not ready.');
        return;
      }
      if (!onCreateStripePaymentIntent) {
        setCardError(PAYMENT_FAILURE_MESSAGE);
        return;
      }
    }

    setShowOrderSubmitOverlay(true);
    try {
      if (checkoutPaymentMethod === 'card' && stripeEnabled) {
        setCardStatus('Preparing secure payment...');
        const intentResult = await onCreateStripePaymentIntent({
          netAmount: stripeCardChargeAmount,
        });
        const clientSecret = intentResult?.clientSecret;
        checkoutOrderRef = String(intentResult?.orderRef || '');

        if (!clientSecret) {
          setCardStatus('');
          setCardError(PAYMENT_FAILURE_MESSAGE);
          return;
        }

        setCardStatus('Confirming card payment...');
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${customerFirstName} ${customerLastName}`.trim(),
              email: customerEmail.trim(),
              phone: customerPhone.trim(),
            },
          },
        });

        if (error) {
          console.error('Card payment confirmation failed:', error);
          setCardStatus('');
          setCardError(getStripeCardErrorMessage(error));
          return;
        }
        if (!paymentIntent || paymentIntent.status !== 'succeeded') {
          setCardStatus('');
          setCardError(PAYMENT_FAILURE_MESSAGE);
          return;
        }
        stripePaymentIntentId = paymentIntent.id || '';
        setCardStatus('Card charged successfully. Finalizing order...');
      }

      await onPlaceOrder({ stripePaymentIntentId, orderRef: checkoutOrderRef });
    } catch (error) {
      console.error('Payment processing failed:', error);
      setCardStatus('');
      setCardError(getStripeCardErrorMessage(error));
    } finally {
      setShowOrderSubmitOverlay(false);
    }
  };

  const renderPaymentMethods = () => {
    if (isPaymentMethodsLoading) {
      return <p className="checkout-inline-note">Loading payment options…</p>;
    }
    if (!paymentMethods.length) {
      return <p className="checkout-inline-error">{PAYMENT_METHODS_UNAVAILABLE_MESSAGE}</p>;
    }

    if (cashOnlyCheckout) {
      return (
        <>
          <div className="payment-option-row payment-option-row--cash-only">
            <div className="payment-option payment-option--static">
              <span className="schedule-dot active">✓</span>
              <span className="payment-option-name">{cashMethod.name}</span>
            </div>
          </div>
          <p className="multi-pay-msg">Pay with cash when you collect your order.</p>
        </>
      );
    }

    return (
      <>
        {hasCard ? (
          <>
            <div className="payment-option-row">
              <button className="payment-option" onClick={() => setCheckoutPaymentMethod('card')}>
                <span className={`schedule-dot ${checkoutPaymentMethod === 'card' ? 'active' : ''}`}>
                  {checkoutPaymentMethod === 'card' ? '✓' : ''}
                </span>
                <span className="payment-option-name">{cardMethod.name}</span>
              </button>
              <small>Secure payment</small>
            </div>
            {checkoutPaymentMethod === 'card' ? (
              <div className="payment-fields">
                <div
                  className="card-number-wrap stripe-card-wrap"
                  role="button"
                  tabIndex={0}
                  onClick={() => cardElementRef?.focus?.()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      cardElementRef?.focus?.();
                    }
                  }}
                >
                  {stripeEnabled && isStripeReady ? (
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#1f1f1f',
                            '::placeholder': { color: '#9a9a9a' },
                          },
                          invalid: { color: '#c71414' },
                        },
                      }}
                      onReady={(element) => setCardElementRef(element)}
                      onChange={(event) => {
                        if (event.error) {
                          setCardError(event.error.message);
                          return;
                        }
                        setCardError('');
                      }}
                    />
                  ) : stripeEnabled ? (
                    <p className="checkout-inline-note">Initializing secure card field...</p>
                  ) : (
                    <p className="checkout-inline-note">Card payment is not available right now.</p>
                  )}
                </div>
                <label className="save-card-toggle">
                  <input type="checkbox" checked={saveCard} onChange={(event) => setSaveCard(event.target.checked)} />
                  <span className="save-card-switch" />
                  <span>Save Card</span>
                </label>
                {cardStatus && !cardError ? <p className="checkout-inline-success">{cardStatus}</p> : null}
                {cardError ? <p className="checkout-inline-error">{cardError}</p> : null}
              </div>
            ) : null}
          </>
        ) : null}
        {hasCash ? (
          <button className="payment-option gift" onClick={() => setCheckoutPaymentMethod('cash')}>
            <span className={`schedule-dot ${checkoutPaymentMethod === 'cash' ? 'active' : ''}`}>
              {checkoutPaymentMethod === 'cash' ? '✓' : ''}
            </span>
            <span className="payment-option-name">{cashMethod.name}</span>
          </button>
        ) : null}
        {hasCash && hasCard ? <p className="multi-pay-msg">Choose your payment method.</p> : null}
      </>
    );
  };

  return (
    <div className="checkout-overlay" style={checkoutOverlayStyle}>
      {showOrderSubmitOverlay ? (
        <div
          className="order-submitting-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-labelledby="order-submitting-title"
          aria-describedby="order-submitting-desc"
        >
          <div className="order-submitting-modal">
            <div className="order-submitting-spinner" aria-hidden />
            <h3 id="order-submitting-title">Please wait</h3>
            <p id="order-submitting-desc">Your order is being processed. Please do not close this page.</p>
          </div>
        </div>
      ) : null}
      <header className="checkout-topbar">
        <button className="menu-btn" aria-label="Open menu" onClick={() => setIsSidebarOpen(true)}>
          ☰
        </button>
        <div className="brand-wrap">
          <button className="brand-home-btn" onClick={() => navigateTo('/')} aria-label="Go to home page">
            <img className="brand-logo" src={logoUrl} alt={storeDisplayName} />
          </button>
        </div>
      </header>
      <div className="checkout-panel">
        <header className="checkout-header">
          <button className="checkout-back" onClick={() => navigateTo('/')} aria-label="Close checkout">
            ‹
          </button>
          <h3>Checkout</h3>
        </header>

        <div className="checkout-section">
          <h4>ORDER TYPE</h4>
          {isDeliveryOrder ? (
            <div className="checkout-collection-only-note">
              <p className="checkout-collection-only-title">
                <strong>Delivery</strong> — from <strong>{storeDisplayName}</strong> to your door.
              </p>
              <div className="checkout-delivery">
                <div className="checkout-postcode-box" ref={postcodeBoxRef}>
                  <div className="checkout-postcode-row">
                    <input
                      type="text"
                      name="deliveryPostcode"
                      autoComplete="postal-code"
                      placeholder="Postcode *"
                      value={deliveryPostcode}
                      className={deliveryPostcodeError ? 'checkout-input-invalid' : ''}
                      aria-invalid={Boolean(deliveryPostcodeError)}
                      aria-autocomplete="list"
                      aria-expanded={isPostcodeListOpen && postcodeSuggestions.length > 0}
                      onChange={(event) => handlePostcodeInputChange(event.target.value)}
                      onFocus={() => setIsPostcodeListOpen(true)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          setIsPostcodeListOpen(false);
                          void onCheckDeliveryPostcode?.(deliveryPostcode);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="checkout-postcode-btn"
                      disabled={isCheckingDeliveryPostcode || !isCompleteUkPostcode(deliveryPostcode)}
                      onClick={() => {
                        setIsPostcodeListOpen(false);
                        void onCheckDeliveryPostcode?.(deliveryPostcode);
                      }}
                    >
                      {isCheckingDeliveryPostcode ? 'Checking…' : 'Find address'}
                    </button>
                  </div>
                  {isPostcodeListOpen && postcodeSuggestions.length > 0 ? (
                    <ul className="checkout-postcode-list" role="listbox">
                      {postcodeSuggestions.map((code) => (
                        <li key={code} role="option" aria-selected={code === normalizePostcode(deliveryPostcode)}>
                          <button type="button" onClick={() => handlePickPostcode(code)}>
                            {code}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {deliveryPostcodeError ? (
                  <span className="checkout-field-error" role="alert">
                    {deliveryPostcodeError}
                  </span>
                ) : null}
                {deliveryAddresses.length > 0 ? (
                  <label className="checkout-field checkout-address-select">
                    <select
                      name="deliveryAddress"
                      value={selectedDeliveryAddressId}
                      onChange={(event) => {
                        setSelectedDeliveryAddressId(event.target.value);
                        setShowAddressRequired(false);
                      }}
                    >
                      <option value="">Select your address ({deliveryAddresses.length} found)</option>
                      {deliveryAddresses.map((address) => (
                        <option key={address.id} value={address.id}>
                          {address.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {deliveryArea ? (
                  <div className="checkout-delivery-info">
                    <p>
                      {deliveryArea.message ||
                        'Congratulations, you are in our delivery area. Please select your address.'}
                    </p>
                    <p>
                      Delivery charge: <strong>{formatPrice(safeDeliveryChargeAmount)}</strong>
                      {deliveryMinimumOrder > 0 ? (
                        <>
                          {' '}
                          · Minimum order: <strong>{formatPrice(deliveryMinimumOrder)}</strong>
                        </>
                      ) : null}
                    </p>
                    {isBelowDeliveryMinimum ? (
                      <p className="checkout-field-error" role="alert">
                        Add {formatPrice(deliveryMinimumOrder - orderSubtotalAmount)} more to your basket to order delivery.
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {showAddressRequired && !selectedDeliveryAddressId ? (
                  <span className="checkout-field-error" role="alert">
                    Please enter your postcode and choose your delivery address.
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="checkout-collection-only-note">
              <p className="checkout-collection-only-title">
                <strong>Click &amp; collect</strong> — order here and pick up at <strong>{storeDisplayName}</strong>.
              </p>
            </div>
          )}
        </div>

        {/* Delivery orders are always ASAP: no date/time picker. */}
        {!isDeliveryOrder ? (
        <div className="checkout-section">
          <h4>SCHEDULED DATE AND TIME</h4>
          {!isScheduleExpanded ? (
            <button className="schedule-summary-btn" onClick={() => setIsScheduleExpanded(true)}>
              <strong>
                {checkoutScheduleType === 'asap'
                  ? 'ASAP'
                  : `${scheduledDate} · ${scheduledTime}`}
              </strong>
              <span>›</span>
            </button>
          ) : (
            <>
              <button className="checkout-time-head" onClick={() => setIsScheduleExpanded(false)}>
                <span />
                <span>▴</span>
              </button>
              <button className="schedule-option" onClick={() => setCheckoutScheduleType('asap')}>
                <div>
                  <strong>ASAP</strong>
                  <small>As soon as possible</small>
                </div>
                <span className={`schedule-dot ${checkoutScheduleType === 'asap' ? 'active' : ''}`}>
                  {checkoutScheduleType === 'asap' ? '✓' : ''}
                </span>
              </button>
              <button className="schedule-option" onClick={() => setCheckoutScheduleType('later')}>
                <div>
                  <strong>Schedule for later</strong>
                  <small>Choose a time</small>
                </div>
                <span className={`schedule-dot ${checkoutScheduleType === 'later' ? 'active' : ''}`}>
                  {checkoutScheduleType === 'later' ? '✓' : ''}
                </span>
              </button>
              {checkoutScheduleType === 'later' ? (
                <div className="schedule-picker-wrap">
                  <label>
                    <span>Date</span>
                    <select value={scheduledDate} onChange={(event) => setScheduledDate(event.target.value)}>
                      {scheduleDateOptions.map((label) => (
                        <option key={label} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Time</span>
                    {isCheckoutSlotsLoading ? (
                      <span className="schedule-time-status">Loading available times…</span>
                    ) : checkoutSlotsError ? (
                      <span className="checkout-inline-error schedule-time-status">{checkoutSlotsError}</span>
                    ) : checkoutSlots.length ? (
                      <select value={scheduledTime} onChange={(event) => setScheduledTime(event.target.value)}>
                        {checkoutSlots.map((slot, idx) => (
                          <option key={`${slot.title}-${idx}`} value={slot.title}>
                            {slot.title}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="checkout-inline-error schedule-time-status">
                        No times are available for this service. Choose ASAP or go back and pick another service.
                      </span>
                    )}
                  </label>
                </div>
              ) : null}
            </>
          )}
        </div>
        ) : null}

        <div className="checkout-section">
          <h4>Your Details</h4>
          <div className="checkout-fields">
            <label className="checkout-field">
              <input
                type="text"
                name="customerFirstName"
                autoComplete="given-name"
                placeholder="Name *"
                value={customerFirstName}
                className={showFieldError('firstName') ? 'checkout-input-invalid' : ''}
                aria-invalid={Boolean(showFieldError('firstName'))}
                aria-describedby={showFieldError('firstName') ? 'checkout-firstName-error' : undefined}
                onChange={(event) => setCustomerFirstName(event.target.value)}
                onBlur={() => markDetailTouched('firstName')}
              />
              {showFieldError('firstName') ? (
                <span id="checkout-firstName-error" className="checkout-field-error" role="alert">
                  {customerDetailsValidation.errors.firstName}
                </span>
              ) : null}
            </label>
            <label className="checkout-field">
              <input
                type="text"
                name="customerLastName"
                autoComplete="family-name"
                placeholder="Family Name"
                value={customerLastName}
                className={showFieldError('lastName') ? 'checkout-input-invalid' : ''}
                aria-invalid={Boolean(showFieldError('lastName'))}
                aria-describedby={showFieldError('lastName') ? 'checkout-lastName-error' : undefined}
                onChange={(event) => setCustomerLastName(event.target.value)}
                onBlur={() => markDetailTouched('lastName')}
              />
              {showFieldError('lastName') ? (
                <span id="checkout-lastName-error" className="checkout-field-error" role="alert">
                  {customerDetailsValidation.errors.lastName}
                </span>
              ) : null}
            </label>
            <label className="checkout-field">
              <input
                type="tel"
                name="customerPhone"
                autoComplete="tel"
                placeholder="Mobile *"
                value={customerPhone}
                className={showFieldError('phone') ? 'checkout-input-invalid' : ''}
                aria-invalid={Boolean(showFieldError('phone'))}
                aria-describedby={showFieldError('phone') ? 'checkout-phone-error' : undefined}
                onChange={(event) => setCustomerPhone(event.target.value)}
                onBlur={() => markDetailTouched('phone')}
              />
              {showFieldError('phone') ? (
                <span id="checkout-phone-error" className="checkout-field-error" role="alert">
                  {customerDetailsValidation.errors.phone}
                </span>
              ) : null}
            </label>
            <label className="checkout-field">
              <input
                type="email"
                name="customerEmail"
                autoComplete="email"
                placeholder="Email *"
                value={customerEmail}
                className={showFieldError('email') ? 'checkout-input-invalid' : ''}
                aria-invalid={Boolean(showFieldError('email'))}
                aria-describedby={showFieldError('email') ? 'checkout-email-error' : undefined}
                onChange={(event) => setCustomerEmail(event.target.value)}
                onBlur={() => markDetailTouched('email')}
              />
              {showFieldError('email') ? (
                <span id="checkout-email-error" className="checkout-field-error" role="alert">
                  {customerDetailsValidation.errors.email}
                </span>
              ) : null}
            </label>
          </div>
        </div>

        <div className="checkout-section">
          <h4>SELECT PAYMENT METHOD</h4>
          {renderPaymentMethods()}
        </div>

        <footer className="checkout-footer">
          {showPlatformFeeOnCheckout || safeDeliveryChargeAmount > 0 ? (
            <div className="checkout-total-row">
              <span>Subtotal</span>
              <strong>{formatPrice(orderSubtotalAmount)}</strong>
            </div>
          ) : null}
          {safeDeliveryChargeAmount > 0 ? (
            <div className="checkout-total-row">
              <span>Delivery</span>
              <strong>{formatPrice(safeDeliveryChargeAmount)}</strong>
            </div>
          ) : null}
          {showPlatformFeeOnCheckout ? (
            <div className="checkout-total-row">
              <span>Platform fee</span>
              <strong>{formatPrice(safePlatformFeeAmount)}</strong>
            </div>
          ) : null}
          <div className="checkout-total-row">
            <span>Total</span>
            <strong>{formatPrice(orderTotalWithSurcharge)}</strong>
          </div>
          {isStripeTestChargeActive && checkoutPaymentMethod === 'card' ? (
            <div className="checkout-total-row checkout-test-charge-note">
              <span>Card charge (test)</span>
              <strong>{formatPrice(stripeCardChargeAmount)}</strong>
            </div>
          ) : null}
        </footer>
        <div className="checkout-sticky-action">
          <button className="place-order-btn" disabled={!canPlaceOrder || isPlacingOrder} onClick={handlePlaceOrderClick}>
            {isPlacingOrder ? 'Placing Order...' : 'Place Order'}
          </button>
          {placeOrderError ? <p className="checkout-inline-error">{placeOrderError}</p> : null}
          {placeOrderSuccess ? <p className="checkout-inline-success">{placeOrderSuccess}</p> : null}
        </div>
      </div>
    </div>
  );
}

function ReviewOrderPageStripeWrapper(props) {
  const stripe = useStripe();
  const elements = useElements();
  return <ReviewOrderPageContent {...props} stripeEnabled stripe={stripe} elements={elements} />;
}

function ReviewOrderPage(props) {
  const paymentMethods = Array.isArray(props.paymentMethods) ? props.paymentMethods : [];
  const hasCardPayment = paymentMethods.some((method) => method.key === 'card');
  const stripePublishableKey = hasCardPayment ? (props.stripePublishableKey || '').trim() : '';
  const stripeConnectAccountId = hasCardPayment ? (props.stripeConnectAccountId || '').trim() : '';
  const stripePromise = useMemo(() => {
    if (!stripePublishableKey) return null;
    const connectOptions = stripeConnectAccountId ? { stripeAccount: stripeConnectAccountId } : undefined;
    return loadStripe(stripePublishableKey, connectOptions);
  }, [stripePublishableKey, stripeConnectAccountId]);

  if (!hasCardPayment || !stripePublishableKey || !stripePromise) {
    return <ReviewOrderPageContent {...props} stripeEnabled={false} stripe={null} elements={null} />;
  }

  return (
    <Elements stripe={stripePromise}>
      <ReviewOrderPageStripeWrapper {...props} />
    </Elements>
  );
}

export default ReviewOrderPage;